import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useSearch } from "@tanstack/react-router";
import { format, parseISO } from "date-fns";
import { Banknote, Pencil, Plus, Trash2 } from "lucide-react";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { EmptyState } from "@/components/shared/empty-state";
import { EntityPicker } from "@/components/shared/entity-picker";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import { Field, FieldGrid } from "@/components/shared/form";
import { PageHeader } from "@/components/shared/page-header";
import { Button } from "@/components/ui/button";
import { Input, NativeSelect } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useClients } from "@/features/clients/api";
import { formatMoney } from "@/features/clients/constants";
import {
  paymentToForm,
  useClientPayments,
  useDeleteClientPayment,
  useIssuedInvoicesForClient,
  useUpsertClientPayment,
} from "@/features/payments/api";
import { PAYMENT_METHODS, labelFor } from "@/features/payments/constants";
import {
  emptyPaymentForm,
  paymentFormSchema,
  type PaymentFormValues,
} from "@/features/payments/schema";
import type { ClientPaymentListRow } from "@/features/payments/types";
import { isSupabaseConfigured } from "@/lib/supabase";
import { blockNumberWheel } from "@/components/shared/line-grid";

export function PaymentsPage({
  clientId: clientIdProp,
  embedded,
  invoiceId: invoiceIdProp,
}: {
  clientId?: string;
  embedded?: boolean;
  invoiceId?: string;
} = {}) {
  return (
    <ErrorBoundary title="Payments crashed">
      <PaymentsInner
        clientIdProp={clientIdProp}
        embedded={embedded}
        invoiceIdProp={invoiceIdProp}
      />
    </ErrorBoundary>
  );
}

function PaymentsInner({
  clientIdProp,
  embedded,
  invoiceIdProp,
}: {
  clientIdProp?: string;
  embedded?: boolean;
  invoiceIdProp?: string;
}) {
  const search = useSearch({ strict: false }) as { client?: string; invoice?: string };
  const clientFilter = clientIdProp ?? search.client;
  const invoiceFilter = invoiceIdProp ?? search.invoice;

  const { data = [], isLoading, error } = useClientPayments({
    clientId: clientFilter,
    invoiceId: invoiceFilter,
  });
  const { data: clients = [] } = useClients();
  const upsert = useUpsertClientPayment();
  const remove = useDeleteClientPayment();

  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<ClientPaymentListRow | null>(null);
  const [deleting, setDeleting] = useState<ClientPaymentListRow | null>(null);

  const form = useForm<PaymentFormValues>({
    resolver: zodResolver(paymentFormSchema),
    defaultValues: {
      ...emptyPaymentForm,
      client_id: clientFilter ?? "",
      invoice_id: invoiceFilter ?? "",
    },
  });

  const watchClientId = form.watch("client_id");
  const { data: issuedInvoices = [] } = useIssuedInvoicesForClient(watchClientId || undefined);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      form.reset(paymentToForm(editing));
    } else {
      form.reset({
        ...emptyPaymentForm,
        client_id: clientFilter ?? "",
        invoice_id: invoiceFilter ?? "",
      });
    }
  }, [open, editing, form, clientFilter, invoiceFilter]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return data;
    return data.filter((row) =>
      [row.client_name, row.client_code, row.reference, row.invoice_number, row.notes]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(query),
    );
  }, [data, q]);

  const total = useMemo(() => filtered.reduce((s, r) => s + r.amount, 0), [filtered]);

  const clientOptions = useMemo(
    () =>
      clients
        .filter((c) => c.is_active)
        .map((c) => ({
          id: c.id,
          label: `${c.code} · ${c.name}`,
          keywords: c.name,
          meta: formatMoney(c.outstanding_balance),
        })),
    [clients],
  );

  const startCreate = useCallback(() => {
    setEditing(null);
    setOpen(true);
  }, []);

  const errors = form.formState.errors;

  async function onSubmit(values: PaymentFormValues) {
    await upsert.mutateAsync({ id: editing?.id, values });
    setOpen(false);
    setEditing(null);
  }

  if (!isSupabaseConfigured) {
    return (
      <EmptyState
        title="Supabase not configured"
        description="Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY to record payments."
      />
    );
  }

  return (
    <div className="space-y-3">
      {!embedded ? (
        <PageHeader
          title="Payments"
          description="Client receipts · reduce AR outstanding"
          actions={
            <Button type="button" size="sm" onClick={startCreate}>
              <Plus />
              Record payment
            </Button>
          }
        />
      ) : (
        <div className="flex justify-end">
          <Button type="button" size="sm" onClick={startCreate}>
            <Plus />
            Record payment
          </Button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Input
          className="h-8 max-w-xs text-xs"
          placeholder="Search client, ref, invoice…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <div className="rounded border bg-card px-3 py-1.5 text-xs">
          Total <strong className="tabular-nums">{formatMoney(total)}</strong>
          <span className="text-muted-foreground"> · {filtered.length}</span>
        </div>
      </div>

      {isLoading ? (
        <p className="py-12 text-center text-sm text-muted-foreground">Loading…</p>
      ) : error ? (
        <EmptyState title="Failed to load" description={(error as Error).message} />
      ) : filtered.length === 0 ? (
        <EmptyState
          title="No payments"
          description="Record a receipt against a client (optionally an invoice)."
          action={
            <Button size="sm" onClick={startCreate}>
              <Plus />
              Record payment
            </Button>
          }
        />
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                {!clientFilter ? <TableHead>Client</TableHead> : null}
                <TableHead>Method</TableHead>
                <TableHead>Reference</TableHead>
                <TableHead>Invoice</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="w-20" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((row) => (
                <TableRow key={row.id} className="text-xs">
                  <TableCell>{format(parseISO(row.payment_date), "dd MMM yyyy")}</TableCell>
                  {!clientFilter ? (
                    <TableCell>
                      <span className="font-mono text-muted-foreground">{row.client_code}</span>{" "}
                      {row.client_name}
                    </TableCell>
                  ) : null}
                  <TableCell>{labelFor(PAYMENT_METHODS, row.payment_method)}</TableCell>
                  <TableCell className="font-mono text-muted-foreground">
                    {row.reference || "—"}
                  </TableCell>
                  <TableCell>
                    {row.invoice_id && row.invoice_number ? (
                      <Link
                        to="/invoices/$invId"
                        params={{ invId: row.invoice_id }}
                        className="font-mono hover:underline"
                      >
                        {row.invoice_number}
                      </Link>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums font-medium">
                    {formatMoney(row.amount)}
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-0.5">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => {
                          setEditing(row);
                          setOpen(true);
                        }}
                      >
                        <Pencil />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => setDeleting(row)}
                      >
                        <Trash2 />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Sheet
        open={open}
        onOpenChange={(v) => {
          setOpen(v);
          if (!v) setEditing(null);
        }}
      >
        <SheetContent className="sm:max-w-md">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Banknote className="size-4" />
              {editing ? "Edit payment" : "Record payment"}
            </SheetTitle>
          </SheetHeader>
          <form
            className="mt-4 space-y-3"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <FieldGrid>
              <Field label="Client" required span={12} error={errors.client_id?.message}>
                <EntityPicker
                  value={form.watch("client_id") ?? ""}
                  disabled={Boolean(clientFilter) && !editing}
                  placeholder="Client…"
                  recentKey="clients"
                  options={clientOptions}
                  onChange={(id) => {
                    form.setValue("client_id", id, { shouldDirty: true, shouldValidate: true });
                    form.setValue("invoice_id", "", { shouldDirty: true });
                  }}
                />
              </Field>
              <Field label="Date" required span={6} error={errors.payment_date?.message}>
                <Input type="date" {...form.register("payment_date")} />
              </Field>
              <Field label="Amount" required span={6} error={errors.amount?.message}>
                <Input
                  type="number"
                  step="0.01"
                  min={0}
                  className="fs-no-spin"
                  onWheel={blockNumberWheel}
                  {...form.register("amount")}
                />
              </Field>
              <Field label="Method" span={6}>
                <NativeSelect {...form.register("payment_method")}>
                  {PAYMENT_METHODS.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field label="Reference" span={6} hint="UTR / cheque no">
                <Input {...form.register("reference")} />
              </Field>
              <Field label="Against invoice" span={12} hint="Optional">
                <NativeSelect
                  {...form.register("invoice_id")}
                  onChange={(e) => {
                    const invId = e.target.value;
                    form.setValue("invoice_id", invId, { shouldDirty: true });
                    const inv = issuedInvoices.find((i) => i.id === invId);
                    if (inv && !editing) {
                      form.setValue("amount", inv.grand_total, { shouldDirty: true });
                    }
                  }}
                >
                  <option value="">— Unallocated —</option>
                  {issuedInvoices.map((inv) => (
                    <option key={inv.id} value={inv.id}>
                      {inv.invoice_number} · {formatMoney(inv.grand_total)}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field label="Notes" span={12}>
                <Textarea rows={2} className="text-xs" {...form.register("notes")} />
              </Field>
            </FieldGrid>
            <SheetFooter className="gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={upsert.isPending}>
                {upsert.isPending ? "Saving…" : "Save · post AR"}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(v) => !v && setDeleting(null)}
        title="Delete payment?"
        description="AR receipt will be reversed and outstanding recalculated."
        confirmLabel="Delete"
        destructive
        loading={remove.isPending}
        onConfirm={async () => {
          if (!deleting) return;
          await remove.mutateAsync(deleting);
          setDeleting(null);
        }}
      />
    </div>
  );
}
